import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { performanceAPI, projectAPI, userAPI, exportAPI, downloadBlob } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getSalesRangeDates, localDateBoundaryIso } from '../lib/dateRanges';
import { Briefcase, TrendingUp, Users, DollarSign, Target as TargetIcon, Clock, CheckCircle2, AlertCircle, Download } from 'lucide-react';

const money = (v) => `\u20b9${Number(v || 0).toLocaleString('en-IN')}`;
const RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'year', label: 'This Year' },
  { key: 'custom', label: 'Custom' },
];

export const MyBusinessPage = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const canViewOthers = user?.role === 'super_admin' || user?.role === 'manager';
  const [users, setUsers] = useState([]);
  const [targetUserId, setTargetUserId] = useState(user?.id || '');
  const [range, setRange] = useState('month');
  const [dateFrom, setDateFrom] = useState(() => getSalesRangeDates('month').from);
  const [dateTo, setDateTo] = useState(() => getSalesRangeDates('month').to);
  const [perf, setPerf] = useState(null);
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (canViewOthers) userAPI.getAll().then(r => setUsers((r.data || []).filter(u => ['sales_executive', 'manager'].includes(u.role)))).catch(() => {}); }, [canViewOthers]);

  useEffect(() => {
    if (range === 'custom') return;
    const r = getSalesRangeDates(range);
    setDateFrom(r.from); setDateTo(r.to);
  }, [range]);

  const load = useCallback(async () => {
    if (!targetUserId) return;
    setLoading(true);
    const params = { date_from: localDateBoundaryIso(dateFrom), date_to: localDateBoundaryIso(dateTo, true) };
    try {
      const [perfRes, projRes] = await Promise.all([
        performanceAPI.get(targetUserId, params),
        projectAPI.list({ assigned_to: targetUserId, ...params }).catch(() => ({ data: { summary: {}, projects: [] } })),
      ]);
      setPerf(perfRes.data);
      setProjects(projRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load your business summary');
      setPerf(null);
    } finally { setLoading(false); }
  }, [targetUserId, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const s = projects?.summary || {};
  const goLeads = (extra = '') => navigate(`/leads${extra}`);
  const goProjects = (extra = '') => navigate(`/projects${extra}`);

  return (
    <div className="space-y-6" data-testid="my-business-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10"><Briefcase className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="text-3xl font-outfit font-semibold">My Business</h1>
          <p className="text-muted-foreground">Your leads, conversions, projects and target — scoped to you.</p>
        </div>
        {hasPermission && hasPermission('report.export') && (
          <button onClick={async () => { try { const res = await exportAPI.download('my-business', { date_from: dateFrom, date_to: dateTo, fmt: 'xlsx' }); downloadBlob(res, 'my-business.xlsx'); toast.success('Export ready'); } catch (e) { toast.error('Export failed'); } }}
            data-testid="mb-export" className="ml-auto inline-flex items-center gap-1 px-3 py-2 text-sm bg-primary/10 text-primary rounded-lg"><Download className="h-4 w-4" /> Export</button>
        )}
      </div>

      <div className="clay-card p-4 flex flex-wrap items-center gap-3">
        {canViewOthers && (
          <select value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} data-testid="mb-user-select"
            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
            <option value={user?.id}>Me ({user?.name})</option>
            {users.filter(u => u.id !== user?.id).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <div className="flex flex-wrap gap-1">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`mb-range-${r.key}`}
              className={`text-sm px-3 py-1.5 rounded-lg border ${range === r.key ? 'bg-primary text-white border-primary' : 'border-slate-200 dark:border-slate-700'}`}>{r.label}</button>
          ))}
        </div>
        {range === 'custom' && (
          <div className="flex gap-2">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="mb-date-from" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="mb-date-to" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
          </div>
        )}
      </div>

      {loading ? <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div> :
       !perf ? <div className="clay-card p-8 text-center text-sm text-muted-foreground">No data.</div> :
       <>
         {/* Lead & conversion cards */}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
           <div onClick={() => goLeads()} className="clay-card p-4 cursor-pointer hover-lift" data-testid="mb-card-leads">
             <div className="flex items-center justify-between mb-1"><p className="text-xs text-muted-foreground">Total Leads</p><Users className="h-4 w-4 text-blue-600" /></div>
             <p className="text-2xl font-outfit font-semibold">{perf.leads?.total ?? 0}</p>
           </div>
           <div className="clay-card p-4" data-testid="mb-card-converted">
             <div className="flex items-center justify-between mb-1"><p className="text-xs text-muted-foreground">Converted Clients</p><CheckCircle2 className="h-4 w-4 text-green-600" /></div>
             <p className="text-2xl font-outfit font-semibold">{perf.sales ?? 0}</p>
           </div>
           <div className="clay-card p-4" data-testid="mb-card-conversion">
             <div className="flex items-center justify-between mb-1"><p className="text-xs text-muted-foreground">Conversion Rate</p><TrendingUp className="h-4 w-4 text-emerald-600" /></div>
             <p className="text-2xl font-outfit font-semibold">{perf.conversion_rate ?? 0}%</p>
           </div>
           <div className="clay-card p-4" data-testid="mb-card-avg-close">
             <div className="flex items-center justify-between mb-1"><p className="text-xs text-muted-foreground">Avg Closing Time</p><Clock className="h-4 w-4 text-purple-600" /></div>
             <p className="text-2xl font-outfit font-semibold">{perf.average_closing_days ?? 0}<span className="text-sm text-muted-foreground"> days</span></p>
           </div>
         </div>

         {/* Business / revenue cards */}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
           <div className="clay-card p-4" data-testid="mb-card-business">
             <div className="flex items-center justify-between mb-1"><p className="text-xs text-muted-foreground">Total Business Generated</p><DollarSign className="h-4 w-4 text-emerald-600" /></div>
             <p className="text-2xl font-outfit font-semibold">{money(perf.revenue)}</p>
           </div>
           <div className="clay-card p-4" data-testid="mb-card-project-value">
             <div className="flex items-center justify-between mb-1"><p className="text-xs text-muted-foreground">Total Project Value</p><Briefcase className="h-4 w-4 text-primary" /></div>
             <p className="text-2xl font-outfit font-semibold">{money((projects?.projects || []).reduce((a, p) => a + (p.project_value || 0), 0))}</p>
           </div>
           <div className="clay-card p-4" data-testid="mb-card-received">
             <div className="flex items-center justify-between mb-1"><p className="text-xs text-muted-foreground">Received Amount</p><DollarSign className="h-4 w-4 text-green-600" /></div>
             <p className="text-2xl font-outfit font-semibold">{money((projects?.projects || []).reduce((a, p) => a + (p.received || 0), 0))}</p>
           </div>
           <div onClick={() => goProjects()} className="clay-card p-4 cursor-pointer hover-lift" data-testid="mb-card-outstanding">
             <div className="flex items-center justify-between mb-1"><p className="text-xs text-muted-foreground">Outstanding Amount</p><AlertCircle className="h-4 w-4 text-orange-600" /></div>
             <p className="text-2xl font-outfit font-semibold text-orange-600">{money(s.total_outstanding)}</p>
           </div>
         </div>

         {/* Projects breakdown */}
         <div>
           <h3 className="text-lg font-outfit font-medium mb-3">Projects</h3>
           <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
             <div onClick={() => goProjects()} className="clay-card p-4 text-center cursor-pointer hover-lift" data-testid="mb-proj-total">
               <p className="text-2xl font-semibold">{s.total ?? 0}</p><p className="text-xs text-muted-foreground mt-1">Total Projects</p>
             </div>
             <div className="clay-card p-4 text-center" data-testid="mb-proj-ongoing">
               <p className="text-2xl font-semibold text-blue-600">{s.ongoing ?? 0}</p><p className="text-xs text-muted-foreground mt-1">Ongoing</p>
             </div>
             <div className="clay-card p-4 text-center" data-testid="mb-proj-completed">
               <p className="text-2xl font-semibold text-green-600">{s.completed ?? 0}</p><p className="text-xs text-muted-foreground mt-1">Completed</p>
             </div>
             <div className="clay-card p-4 text-center" data-testid="mb-proj-waiting">
               <p className="text-2xl font-semibold text-amber-600">{s.waiting_approval ?? 0}</p><p className="text-xs text-muted-foreground mt-1">Pending Approval</p>
             </div>
             <div onClick={() => goProjects()} className="clay-card p-4 text-center cursor-pointer hover-lift" data-testid="mb-proj-payment-pending">
               <p className="text-2xl font-semibold text-orange-600">{s.payment_pending ?? 0}</p><p className="text-xs text-muted-foreground mt-1">Payment Pending</p>
             </div>
           </div>
         </div>

         {/* Target performance */}
         <div>
           <h3 className="text-lg font-outfit font-medium mb-3">Target & Achievement</h3>
           <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="mb-target">
             <div className="clay-card p-4"><p className="text-xs text-muted-foreground">Target</p><p className="text-xl font-semibold mt-1">{money(perf.revenue_target)}</p></div>
             <div className="clay-card p-4"><p className="text-xs text-muted-foreground">Achieved</p><p className="text-xl font-semibold mt-1">{money(perf.revenue)}</p></div>
             <div className="clay-card p-4"><p className="text-xs text-muted-foreground">Remaining</p><p className="text-xl font-semibold mt-1">{money(perf.remaining_target)}</p></div>
             <div className="clay-card p-4"><p className="text-xs text-muted-foreground">Achievement %</p><p className="text-xl font-semibold mt-1 flex items-center gap-1"><TargetIcon className="h-4 w-4 text-primary" />{perf.target_achievement_pct ?? 0}%</p></div>
             <div className="clay-card p-4"><p className="text-xs text-muted-foreground">Expected Pipeline</p><p className="text-xl font-semibold mt-1">{money(perf.expected_pipeline)}</p></div>
           </div>
         </div>
       </>}
    </div>
  );
};
export default MyBusinessPage;
